import React, { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useAppStore } from "../stores/appStore";
import { useProjectData } from "../stores/appStore";
import { STARTUP_TEMPLATES } from "../data/templates";
import { PageLayout } from "./PageLayout";
import { ThemeToggle } from "./theme-toggle";
import {
  OnboardingStep,
  OnboardingStepContent,
  OnboardingNavigation,
} from "./onboarding/OnboardingComponents";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
  Building2,
  Users,
  DollarSign,
  TrendingUp,
  Rocket,
  ArrowRight,
  X,
  Play,
  Zap,
} from "lucide-react";

import TeamStructureBuilder from "./TeamStructureBuilder";
import OperatingCostsManager from "./OperatingCostsManager";

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  required: boolean;
}

const steps: OnboardingStep[] = [
  {
    id: "welcome",
    title: "Welcome",
    description: "Choose your startup template",
    icon: Sparkles,
    required: true,
  },
  {
    id: "company",
    title: "Company & Business Setup",
    description: "Company profile and business configuration",
    icon: Building2,
    required: true,
  },
  {
    id: "team",
    title: "Team Structure",
    description: "Build your founding team",
    icon: Users,
    required: true,
  },
  {
    id: "costs",
    title: "Operating Costs",
    description: "Set up your cost structure",
    icon: DollarSign,
    required: true,
  },
  {
    id: "investment",
    title: "Investment Scenario",
    description: "Configure funding and valuation",
    icon: TrendingUp,
    required: false,
  },
  {
    id: "launch",
    title: "Launch",
    description: "Review and start building",
    icon: Rocket,
    required: true,
  },
];

interface OnboardingData {
  companyProfile: {
    name: string;
    industry: string;
    location: string;
    description: string;
  };
  currency?: string;
  country?: string;
  soloPrice?: number;
  teamPrice?: number;
  unlimitedPrice?: number;
  selectedTemplate?: (typeof STARTUP_TEMPLATES)[0];
  investmentScenario: {
    seekingInvestment: boolean;
    investmentAmount: number;
    currentValuation: number;
    postMoneyValuation: number;
    equityStake: number;
  };
  completedSteps: string[];
}

interface UnifiedOnboardingProps {
  fullScreen?: boolean;
}

export default function UnifiedOnboarding({
  fullScreen = false,
}: UnifiedOnboardingProps) {
  // Detect if we're on the /new route for full screen mode
  const isNewRoute =
    typeof window !== "undefined" && window.location.pathname === "/new";
  const shouldBeFullScreen = fullScreen || isNewRoute;

  const { setTeamRoles, setOperatingCosts, setCompanyProfile, planningMonths } = useAppStore();
  const { setProjectData } = useProjectData();
  const [currentStep, setCurrentStep] = useState(0);
  const [startupId, setStartupId] = useState<string | null>(null);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    companyProfile: {
      name: "",
      industry: "",
      location: "",
      description: "",
    },
    currency: "INR",
    country: "India",
    soloPrice: 99,
    teamPrice: 299,
    unlimitedPrice: 999,
    investmentScenario: {
      seekingInvestment: false,
      investmentAmount: 0,
      currentValuation: 0,
      postMoneyValuation: 0,
      equityStake: 0,
    },
    completedSteps: [],
  });

  // Refs for maintaining focus in form inputs
  const nameRef = React.useRef<HTMLInputElement>(null);
  const locationRef = React.useRef<HTMLInputElement>(null);
  const descriptionRef = React.useRef<HTMLTextAreaElement>(null);

  // Currency symbol mapping
  const getCurrencySymbol = useCallback((currency: string) => {
    const symbols: Record<string, string> = {
      INR: "₹",
      USD: "$",
      AED: "د.إ",
      EUR: "€",
      GBP: "£",
    };
    return symbols[currency] || currency;
  }, []);

  // Load startup data if available
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const startupId = urlParams.get('startupId');

    if (startupId) {
      const savedStartups = localStorage.getItem('userStartups');
      if (savedStartups) {
        try {
          const startups = JSON.parse(savedStartups);
          const startup = startups.find((s: any) => s.id === startupId);

          if (startup) {
            // Pre-fill company profile with startup data
            // Priority: startup.data.companyProfile > startup root fields
            const companyData = {
              name: startup.data?.companyProfile?.name || startup.name || "",
              industry: startup.data?.companyProfile?.industry || "",
              location: startup.data?.companyProfile?.location || "",
              description: startup.data?.companyProfile?.description || startup.description || "",
            };
            setOnboardingData(prev => ({
              ...prev,
              companyProfile: companyData,
              currency: startup.data?.currency || "INR",
              country: startup.data?.country || "India",
              soloPrice: startup.data?.soloPrice || 99,
              teamPrice: startup.data?.teamPrice || 299,
              unlimitedPrice: startup.data?.unlimitedPrice || 999,
            }));
            
            // Also update project data in store
            if (companyData.name) {
              setProjectData({ 
                projectName: companyData.name,
                description: companyData.description,
                industry: companyData.industry 
              });
            }
          }
        } catch (error) {
          console.error('Error loading startup data:', error);
        }
      }
    }
  }, []);



  const updateCompanyField = useCallback(
    (field: keyof OnboardingData["companyProfile"], value: string) => {
      setOnboardingData((prev) => ({
        ...prev,
        companyProfile: { ...prev.companyProfile, [field]: value },
      }));
    },
    [],
  );

  const selectTemplate = useCallback(
    (template: (typeof STARTUP_TEMPLATES)[0]) => {
      setOnboardingData((prev) => ({
        ...prev,
        selectedTemplate: template,
        companyProfile: {
          ...prev.companyProfile,
          name: template.companyProfile?.name || template.name,
          industry: template.companyProfile?.industry || template.industry,
          location: template.companyProfile?.location || "San Francisco, CA",
        },
      }));
    },
    [],
  );

  const launchToMainApp = useCallback(() => {
    // Apply the onboarding data to the main app
    if (onboardingData.selectedTemplate) {
      setTeamRoles(onboardingData.selectedTemplate.teamRoles || []);

      // Map operating costs to include required frequency property
      const mappedCosts = (
        onboardingData.selectedTemplate.operatingCosts || []
      ).map((cost) => ({
        ...cost,
        frequency: "monthly" as const, // Default to monthly
        monthlyCosts: cost.monthlyCosts || [],

        // Add default values for missing properties
        isOneTime: cost.isOneTime || false,
        oneTimeMonth: cost.oneTimeMonth || 1,
        resourceCount: cost.resourceCount || 1,
        customAmount: cost.customAmount || 0,
      }));
      setOperatingCosts(mappedCosts);

      setCompanyProfile({
        ...onboardingData.selectedTemplate.companyProfile,
        ...onboardingData.companyProfile,
      });
    }

    // Save startup data to localStorage
    const targetStartupId = startupId || Date.now().toString();
    
    if (true) { // Always execute save logic
      const savedStartups = localStorage.getItem("userStartups");
      let startups = [];
      if (savedStartups) {
        try {
          startups = JSON.parse(savedStartups);
        } catch (error) {
          console.error("Error parsing startups:", error);
          startups = [];
        }
      }

      const startupIndex = startups.findIndex((s: any) => s.id === targetStartupId);
      const teamSize =
        onboardingData.selectedTemplate?.teamRoles?.reduce(
          (sum, role) =>
            sum + role.headcount.reduce((roleSum, count) => roleSum + count, 0),
          0,
        ) || 0;

      const startupData = {
        selectedTemplate: onboardingData.selectedTemplate,
        companyProfile: onboardingData.companyProfile,
        currency: onboardingData.currency,
        country: onboardingData.country,
        soloPrice: onboardingData.soloPrice,
        teamPrice: onboardingData.teamPrice,
        unlimitedPrice: onboardingData.unlimitedPrice,
        investmentScenario: onboardingData.investmentScenario,
        completedSteps: [
          ...onboardingData.completedSteps,
          steps[currentStep].id,
        ],
        // Save current store data
        teamRoles: useAppStore.getState().teamRoles,
        operatingCosts: useAppStore.getState().operatingCosts,
      };

      if (startupIndex >= 0) {
        // Update existing startup
        startups[startupIndex] = {
          ...startups[startupIndex],
          status: "completed",
          updatedAt: new Date(),
          data: startupData,
          metrics: {
            teamSize,
            // Add more metrics as needed
          },
        };
      } else {
        // Create new startup
        startups.push({
          id: targetStartupId,
          name: onboardingData.companyProfile.name || "New Startup",
          description: onboardingData.companyProfile.description,
          status: "completed",
          createdAt: new Date(),
          updatedAt: new Date(),
          data: startupData,
          metrics: {
            teamSize,
          },
        });
      }

      localStorage.setItem("userStartups", JSON.stringify(startups));
    }

    // Set investment data if seeking investment
    if (onboardingData.investmentScenario.seekingInvestment) {
      // ... existing investment data logic ...
    }

    // Navigate to main app
    window.location.href = `/?startupId=${targetStartupId}`;
  }, [onboardingData, startupId, currentStep, setTeamRoles, setOperatingCosts, setCompanyProfile]);

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  // Validation logic for proceeding to next step
  const canProceedToNext = useCallback(() => {
    switch (currentStepData.id) {
      case "welcome":
        return !!onboardingData.selectedTemplate;
      case "company":
        // For company step, check if required fields are filled
        return !!(
          onboardingData.companyProfile.name.trim() &&
          onboardingData.companyProfile.industry &&
          onboardingData.companyProfile.location.trim()
        );
      case "team":
      case "costs":
      case "investment":
        return true; // These steps are optional or have defaults
      case "launch":
        return true;
      default:
        return false;
    }
  }, [currentStepData.id, onboardingData.selectedTemplate, onboardingData.companyProfile]);

  const nextStep = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
      setOnboardingData((prev) => ({
        ...prev,
        completedSteps: [...prev.completedSteps, steps[currentStep].id],
      }));
    } else {
      launchToMainApp();
    }
  }, [currentStep, launchToMainApp]);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const goToStep = useCallback((stepIndex: number) => {
    setCurrentStep(stepIndex);
  }, []);

  // Step Components
  const WelcomeStep = () => (
    <OnboardingStepContent step={steps[0]}>
      <div className="space-y-8">
        <div className="color-info-bg rounded-xl p-6 border border-blue-200/50 dark:border-blue-800/50 shadow-sm animate-fade-in">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="space-y-2 flex-1">
              <h3 className="font-semibold text-lg text-blue-900 dark:text-blue-100">
                Choose Your Starting Point
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed">
                Select a template that matches your startup type, or create a custom
                setup. Each template comes pre-configured with realistic assumptions
                for your industry.
              </p>
            </div>
          </div>
        </div>

        {/* Template Selection */}
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STARTUP_TEMPLATES.map((template, index) => {
              const isSelected = onboardingData.selectedTemplate?.id === template.id;

              return (
                <Card
                  key={template.id}
                  className={`cursor-pointer transition-all duration-300 ease-out transform group ${
                    isSelected
                      ? "ring-2 ring-blue-500 shadow-xl scale-[1.02] bg-blue-50/30 dark:bg-blue-950/10 border-blue-200 dark:border-blue-800"
                      : "hover:shadow-xl hover:scale-[1.01] hover:-translate-y-1 border-slate-200 dark:border-slate-800"
                  }`}
                  onClick={() => selectTemplate(template)}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-lg font-semibold group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {template.name}
                      </CardTitle>
                      <Badge variant="secondary" className="font-medium">
                        {template.industry}
                      </Badge>
                    </div>
                    <CardDescription className="text-sm leading-relaxed">
                      {template.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2.5 text-sm text-slate-600 dark:text-slate-400">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                          <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <span className="font-medium">{template.teamRoles?.length || 0} team members</span>
                      </div>
                      <div className="flex items-center gap-2.5 text-sm text-slate-600 dark:text-slate-400">
                        <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                          <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </div>
                        <span className="font-medium">{template.operatingCosts?.length || 0} cost categories</span>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800">
                        <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 text-sm font-medium animate-fade-in">
                          <Check className="w-4 h-4" />
                          Selected
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center pt-6 animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <Button 
              variant="outline" 
              className="w-full max-w-xs transition-all duration-300 hover:scale-105 hover:shadow-lg hover:border-blue-400 dark:hover:border-blue-600 border-2 font-medium group"
            >
              <Zap className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
              Start from Scratch
            </Button>
            <p className="text-sm text-muted-foreground mt-3">
              Complete control over every aspect of your startup model
            </p>
          </div>
        </div>
      </div>
    </OnboardingStepContent>
  );

  const CompanyStep = () => {
    // Use refs for uncontrolled inputs to avoid re-render issues
    const nameRef = useRef<HTMLInputElement>(null);
    const industryRef = useRef<HTMLButtonElement>(null);
    const locationRef = useRef<HTMLInputElement>(null);
    const descriptionRef = useRef<HTMLTextAreaElement>(null);
    const currencyRef = useRef<HTMLButtonElement>(null);
    const countryRef = useRef<HTMLButtonElement>(null);
    const soloPriceRef = useRef<HTMLInputElement>(null);
    const teamPriceRef = useRef<HTMLInputElement>(null);
    const unlimitedPriceRef = useRef<HTMLInputElement>(null);

    // Sync refs with onboardingData when it changes
    useEffect(() => {
      if (nameRef.current) nameRef.current.value = onboardingData.companyProfile.name;
      if (locationRef.current) locationRef.current.value = onboardingData.companyProfile.location;
      if (descriptionRef.current) descriptionRef.current.value = onboardingData.companyProfile.description;
      if (soloPriceRef.current) soloPriceRef.current.value = onboardingData.soloPrice?.toString() || '';
      if (teamPriceRef.current) teamPriceRef.current.value = onboardingData.teamPrice?.toString() || '';
      if (unlimitedPriceRef.current) unlimitedPriceRef.current.value = onboardingData.unlimitedPrice?.toString() || '';
    }, [onboardingData]);

    // Handle form field changes - sync to parent state on blur
    const handleFieldBlur = useCallback((field: string, ref: React.RefObject<any>) => {
      if (!ref.current) return;

      const value = ref.current.value;
      console.log('Field blur:', field, value);

      if (field === 'name' || field === 'industry' || field === 'location' || field === 'description') {
        setOnboardingData(prev => ({
          ...prev,
          companyProfile: { ...prev.companyProfile, [field]: value }
        }));
      } else if (field === 'soloPrice' || field === 'teamPrice' || field === 'unlimitedPrice') {
        setOnboardingData(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
      } else {
        setOnboardingData(prev => ({ ...prev, [field]: value }));
      }
    }, []);

    // Handle select changes immediately (since they don't have blur events like inputs)
    const handleSelectChange = useCallback((field: string, value: string) => {
      console.log('Select change:', field, value);

      if (field === 'industry') {
        setOnboardingData(prev => ({
          ...prev,
          companyProfile: { ...prev.companyProfile, industry: value }
        }));
      } else {
        setOnboardingData(prev => ({ ...prev, [field]: value }));
      }
    }, []);



    return (
      <OnboardingStepContent step={steps[1]}>
        <div className="space-y-8">
          <div className="color-info-bg rounded-xl p-6 border border-blue-200/50 dark:border-blue-800/50 shadow-sm animate-fade-in">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div className="space-y-2 flex-1">
                <h3 className="font-semibold text-lg text-blue-900 dark:text-blue-100">
                  Company & Business Setup
                </h3>
                <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed">
                  Set up your company profile and business configuration. These foundational
                  settings will be used throughout your financial planning.
                </p>
              </div>
            </div>
          </div>

          <form className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Company Profile */}
            <Card className="animate-fade-in-up border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow duration-300">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold">Company Profile</CardTitle>
                <CardDescription className="text-sm">
                  Basic information about your startup
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                  <Label htmlFor="companyName" className="text-sm font-medium">Company Name *</Label>
                   <Input
                     ref={nameRef}
                     id="companyName"
                     placeholder="e.g., TechCorp Inc."
                     defaultValue={onboardingData.companyProfile.name}
                     onBlur={() => handleFieldBlur('name', nameRef)}
                     className="transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                   />
                </div>

                <div className="space-y-2 animate-fade-in" style={{ animationDelay: '0.15s' }}>
                  <Label htmlFor="industry" className="text-sm font-medium">Industry *</Label>
                   <Select
                     defaultValue={onboardingData.companyProfile.industry}
                     onValueChange={(value) => handleSelectChange('industry', value)}
                   >
                     <SelectTrigger className="transition-all duration-200 focus:ring-2 focus:ring-blue-500">
                       <SelectValue placeholder="Select your industry" />
                     </SelectTrigger>
                     <SelectContent>
                       <SelectItem value="SaaS">SaaS</SelectItem>
                       <SelectItem value="E-commerce">E-commerce</SelectItem>
                       <SelectItem value="AI/ML">AI/ML</SelectItem>
                       <SelectItem value="Fintech">Fintech</SelectItem>
                       <SelectItem value="Healthtech">Healthtech</SelectItem>
                       <SelectItem value="Edtech">Edtech</SelectItem>
                       <SelectItem value="Other">Other</SelectItem>
                     </SelectContent>
                   </Select>
                </div>

                <div className="space-y-2 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                  <Label htmlFor="location" className="text-sm font-medium">Location *</Label>
                   <Input
                     ref={locationRef}
                     id="location"
                     placeholder="e.g., San Francisco, CA"
                     defaultValue={onboardingData.companyProfile.location}
                     onBlur={() => handleFieldBlur('location', locationRef)}
                     className="transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                   />
                </div>

                <div className="space-y-2 animate-fade-in" style={{ animationDelay: '0.25s' }}>
                  <Label htmlFor="description" className="text-sm font-medium">Brief Description (Optional)</Label>
                   <textarea
                     ref={descriptionRef}
                     className="w-full p-3 border border-input rounded-md bg-background text-sm transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                     rows={3}
                     placeholder="Describe what your startup does..."
                     defaultValue={onboardingData.companyProfile.description}
                     onBlur={() => handleFieldBlur('description', descriptionRef)}
                   />
                </div>
              </CardContent>
            </Card>

            {/* Business Configuration */}
            <Card className="animate-fade-in-up border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow duration-300" style={{ animationDelay: '0.1s' }}>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold">Business Configuration</CardTitle>
                <CardDescription className="text-sm">
                  Set up your business parameters and pricing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Regional Settings */}
                <div className="space-y-4 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                  <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                    Regional Settings
                  </h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="currency" className="text-sm font-medium">Primary Currency</Label>
                       <Select
                         defaultValue={onboardingData.currency || 'INR'}
                         onValueChange={(value) => handleSelectChange('currency', value)}
                       >
                        <SelectTrigger className="h-10 transition-all duration-200 focus:ring-2 focus:ring-blue-500">
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="INR">INR (₹) - India</SelectItem>
                          <SelectItem value="USD">USD ($) - United States</SelectItem>
                          <SelectItem value="AED">AED (د.إ) - UAE</SelectItem>
                          <SelectItem value="EUR">EUR (€) - Europe</SelectItem>
                          <SelectItem value="GBP">GBP (£) - United Kingdom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-sm font-medium">Primary Country</Label>
                       <Select
                         defaultValue={onboardingData.country || 'India'}
                         onValueChange={(value) => handleSelectChange('country', value)}
                       >
                        <SelectTrigger className="h-10 transition-all duration-200 focus:ring-2 focus:ring-blue-500">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="India">India</SelectItem>
                          <SelectItem value="United States">United States</SelectItem>
                          <SelectItem value="United Arab Emirates">United Arab Emirates</SelectItem>
                          <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                          <SelectItem value="Germany">Germany</SelectItem>
                          <SelectItem value="France">France</SelectItem>
                          <SelectItem value="Canada">Canada</SelectItem>
                          <SelectItem value="Australia">Australia</SelectItem>
                          <SelectItem value="Singapore">Singapore</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Pricing Strategy */}
                <div className="space-y-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                  <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                    Pricing Strategy
                  </h4>
                  <div className="space-y-4">
                    <div className="space-y-2 p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 transition-all duration-200 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-slate-100 dark:hover:bg-slate-900/70">
                       <Label htmlFor="soloPrice" className="text-sm font-medium">Solo Plan ({getCurrencySymbol(onboardingData.currency || 'INR')})</Label>
                       <Input
                         ref={soloPriceRef}
                         id="soloPrice"
                         type="number"
                         placeholder="99"
                         defaultValue={onboardingData.soloPrice || 99}
                         onBlur={() => handleFieldBlur('soloPrice', soloPriceRef)}
                         className="h-10 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                       />
                      <p className="text-xs text-muted-foreground">Individual users</p>
                    </div>

                    <div className="space-y-2 p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 transition-all duration-200 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-slate-100 dark:hover:bg-slate-900/70">
                       <Label htmlFor="teamPrice" className="text-sm font-medium">Team Plan ({getCurrencySymbol(onboardingData.currency || 'INR')})</Label>
                       <Input
                         ref={teamPriceRef}
                         id="teamPrice"
                         type="number"
                         placeholder="299"
                         defaultValue={onboardingData.teamPrice || 299}
                         onBlur={() => handleFieldBlur('teamPrice', teamPriceRef)}
                         className="h-10 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                       />
                      <p className="text-xs text-muted-foreground">5+ users</p>
                    </div>

                    <div className="space-y-2 p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 transition-all duration-200 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-slate-100 dark:hover:bg-slate-900/70">
                       <Label htmlFor="unlimitedPrice" className="text-sm font-medium">Enterprise Plan ({getCurrencySymbol(onboardingData.currency || 'INR')})</Label>
                       <Input
                         ref={unlimitedPriceRef}
                         id="unlimitedPrice"
                         type="number"
                         placeholder="999"
                         defaultValue={onboardingData.unlimitedPrice || 999}
                         onBlur={() => handleFieldBlur('unlimitedPrice', unlimitedPriceRef)}
                         className="h-10 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                       />
                      <p className="text-xs text-muted-foreground">Unlimited usage</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </form>
        </div>
      </OnboardingStepContent>
    );
  };

  // Placeholder components for other steps
  const TeamStep = () => (
    <OnboardingStepContent step={steps[2]}>
      <div className="space-y-6">
        <div className="color-info-bg rounded-xl p-6 border border-blue-200/50 dark:border-blue-800/50 shadow-sm animate-fade-in">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div className="space-y-2 flex-1">
              <h3 className="font-semibold text-lg text-blue-900 dark:text-blue-100">
                Team Planning Tool
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed">
                Define your founding team, set salaries, and plan your hiring
                timeline. Use the table below to add roles and configure
                headcount for each month.
              </p>
            </div>
          </div>
        </div>
        <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <TeamStructureBuilder />
        </div>
      </div>
    </OnboardingStepContent>
  );

  const CostsStep = () => (
    <OnboardingStepContent step={steps[3]}>
      <div className="space-y-6">
        <div className="color-info-bg rounded-xl p-6 border border-green-200/50 dark:border-green-800/50 shadow-sm animate-fade-in">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-green-500/20">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <div className="space-y-2 flex-1">
              <h3 className="font-semibold text-lg text-green-900 dark:text-green-100">
                Operating Costs Setup
              </h3>
              <p className="text-sm text-green-800 dark:text-green-200 leading-relaxed">
                Configure your monthly operating expenses including salaries,
                tools, marketing, and other recurring costs.
              </p>
            </div>
          </div>
        </div>
        <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <OperatingCostsManager planningMonths={planningMonths} />
        </div>
      </div>
    </OnboardingStepContent>
  );

  const InvestmentStep = () => {
    const VALUATION_PRESETS = [
      { id: "pre-seed", name: "Pre-Seed (Idea Stage)", valuation: 1000000, investment: 150000, equity: 15 },
      { id: "seed", name: "Seed (MVP Traction)", valuation: 5000000, investment: 1000000, equity: 20 },
      { id: "series-a", name: "Series A (Growth)", valuation: 15000000, investment: 3000000, equity: 20 },
    ];

    const applyPreset = (presetId: string) => {
      const preset = VALUATION_PRESETS.find(p => p.id === presetId);
      if (preset) {
        setOnboardingData(prev => ({
          ...prev,
          investmentScenario: {
            ...prev.investmentScenario,
            investmentAmount: preset.investment,
            postMoneyValuation: preset.valuation,
            equityStake: preset.equity,
            currentValuation: preset.valuation - preset.investment
          }
        }));
      }
    };

    return (
    <OnboardingStepContent step={steps[4]}>
      <div className="space-y-6">
        <div className="color-info-bg rounded-xl p-6 border border-blue-200/50 dark:border-blue-800/50 shadow-sm animate-fade-in">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div className="space-y-2 flex-1">
              <h3 className="font-semibold text-lg text-blue-900 dark:text-blue-100">
                Investment Scenario
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed">
                Configure your funding requirements and valuation
                expectations. Choose a preset or set custom terms - all fields
                are fully configurable.
              </p>
            </div>
          </div>
        </div>

        <Card className="max-w-2xl mx-auto border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <CardContent className="p-6 space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="investment-type" className="text-sm font-medium">
                  Investment Status
                </Label>
                <Select value={onboardingData.investmentScenario.seekingInvestment ? "seeking" : "not-seeking"} onValueChange={(value) => {
                  if (value === "not-seeking") {
                    setOnboardingData(prev => ({
                      ...prev,
                      investmentScenario: {
                        ...prev.investmentScenario,
                        seekingInvestment: false
                      }
                    }));
                  } else {
                    setOnboardingData(prev => ({
                      ...prev,
                      investmentScenario: {
                        ...prev.investmentScenario,
                        seekingInvestment: true
                      }
                    }));
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select investment status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-seeking">
                      Not seeking investment
                    </SelectItem>
                    <SelectItem value="seeking">
                      Seeking investment
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {onboardingData.investmentScenario.seekingInvestment && (
                <div className="space-y-4 pt-4 border-t animate-in fade-in slide-in-from-top-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Valuation Preset</Label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {VALUATION_PRESETS.map((preset, index) => {
                        const isSelected = onboardingData.investmentScenario.postMoneyValuation === preset.valuation;
                        return (
                          <div
                            key={preset.id}
                            className={`
                              cursor-pointer rounded-xl border p-4 transition-all duration-300 transform
                              ${isSelected 
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500 shadow-lg scale-[1.02]' 
                                : 'border-slate-200 dark:border-slate-800 hover:border-blue-400 dark:hover:border-blue-600 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 hover:shadow-md hover:scale-[1.01]'
                              }
                            `}
                            onClick={() => applyPreset(preset.id)}
                            style={{ animationDelay: `${index * 0.05}s` }}
                          >
                            <div className="font-semibold text-sm mb-1">{preset.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {getCurrencySymbol(onboardingData.currency || 'INR')}{(preset.valuation / 1000000).toFixed(1)}M Val
                            </div>
                            {isSelected && (
                              <div className="mt-2 flex items-center gap-1.5 text-blue-600 dark:text-blue-400 text-xs font-medium animate-fade-in">
                                <Check className="w-3 h-3" />
                                Selected
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                      <Label htmlFor="investmentAmount" className="text-sm font-medium">Investment Amount</Label>
                      <Input
                        id="investmentAmount"
                        type="number"
                        value={onboardingData.investmentScenario.investmentAmount}
                        onChange={(e) => setOnboardingData(prev => ({
                          ...prev,
                          investmentScenario: {
                            ...prev.investmentScenario,
                            investmentAmount: parseFloat(e.target.value) || 0
                          }
                        }))}
                        className="transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="space-y-2 animate-fade-in" style={{ animationDelay: '0.25s' }}>
                      <Label htmlFor="valuation" className="text-sm font-medium">Post-Money Valuation</Label>
                      <Input
                        id="valuation"
                        type="number"
                        value={onboardingData.investmentScenario.postMoneyValuation}
                        onChange={(e) => setOnboardingData(prev => ({
                          ...prev,
                          investmentScenario: {
                            ...prev.investmentScenario,
                            postMoneyValuation: parseFloat(e.target.value) || 0
                          }
                        }))}
                        className="transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </OnboardingStepContent>
    );
  };

  const LaunchStep = () => (
    <OnboardingStepContent step={steps[5]}>
      <div className="space-y-8">
        <div className="text-center space-y-6 animate-fade-in-down">
          <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto shadow-2xl shadow-green-500/30 animate-scale-in">
            <Rocket className="w-12 h-12 text-white" />
          </div>
          <div>
            <h3 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-700 dark:from-green-400 dark:to-green-500 bg-clip-text text-transparent mb-3">
              Ready to Launch!
            </h3>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-xl mx-auto leading-relaxed">
              Your startup foundation is complete. Let's start building your financial model.
            </p>
          </div>
        </div>

        <Card className="max-w-2xl mx-auto border-slate-200 dark:border-slate-800 shadow-lg animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <CardContent className="p-8">
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md shadow-green-500/20">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <span className="text-sm font-semibold text-green-800 dark:text-green-200">
                  Company profile configured
                </span>
              </div>
              <div className="flex items-center gap-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md shadow-green-500/20">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <span className="text-sm font-semibold text-green-800 dark:text-green-200">
                  Business settings established
                </span>
              </div>
              <div className="flex items-center gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 animate-fade-in" style={{ animationDelay: '0.5s' }}>
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md shadow-blue-500/20">
                  <Play className="w-5 h-5 text-white" />
                </div>
                <span className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                  Ready to configure team and costs
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </OnboardingStepContent>
  );

  // Render current step with smooth transitions
  const renderCurrentStep = useCallback(() => {
    switch (currentStepData.id) {
      case "welcome":
        return <WelcomeStep key={`welcome-step-${currentStep}`} />;
      case "company":
        return <CompanyStep key={`company-step-${currentStep}`} />;
      case "team":
        return <TeamStep key={`team-step-${currentStep}`} />;
      case "costs":
        return <CostsStep key={`costs-step-${currentStep}`} />;
      case "investment":
        return <InvestmentStep key={`investment-step-${currentStep}`} />;
      case "launch":
        return <LaunchStep key={`launch-step-${currentStep}`} />;
      default:
        return null;
    }
  }, [currentStepData.id, currentStep, onboardingData]);

  if (shouldBeFullScreen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.03] bg-[radial-gradient(circle_at_1px_1px,_currentColor_1px,_transparent_0)] bg-[length:24px_24px] [color:currentColor]"></div>
        
        <div className="container mx-auto px-6 py-8 max-w-5xl relative z-10">
          <div className="mb-10 animate-fade-in-down">
            <div className="flex items-center justify-between mb-6">
              <div className="space-y-1">
                <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-slate-100 dark:via-slate-200 dark:to-slate-100 bg-clip-text text-transparent tracking-tight">
                  Startup Setup
                </h1>
                <p className="text-slate-600 dark:text-slate-400 text-lg">
                  Let's build your startup foundation together
                </p>
              </div>
              <div className="animate-scale-in" style={{ animationDelay: '0.1s' }}>
                <ThemeToggle />
              </div>
            </div>
            
            {/* Enhanced Progress Bar */}
            <div className="space-y-3">
              <div className="relative h-2.5 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden shadow-inner">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500 rounded-full transition-all duration-500 ease-out shadow-lg shadow-blue-500/30 relative overflow-hidden"
                  style={{ width: `${progress}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                </div>
              </div>
              <div className="flex justify-between text-sm font-medium text-slate-600 dark:text-slate-400">
                <span className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-semibold">
                    {currentStep + 1}
                  </span>
                  Step {currentStep + 1} of {steps.length}
                </span>
                <span className="text-slate-900 dark:text-slate-100 font-semibold">
                  {currentStepData.title}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-8 relative">
            <div key={currentStep} className="animate-fade-in-up step-transition">
              {renderCurrentStep()}
            </div>
          </div>

           <OnboardingNavigation
             currentStep={currentStep}
             totalSteps={steps.length}
             onNext={nextStep}
             onPrev={prevStep}
             canGoNext={canProceedToNext()}
             canGoPrev={currentStep > 0}
             nextLabel={currentStep === steps.length - 1 ? "Launch Project" : "Next"}
           />
        </div>
      </div>
    );
  }

  return (
    <PageLayout title="Startup Onboarding" description="Build your startup foundation">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4">Startup Onboarding</h1>
          <Progress value={progress} className="h-2" />
          <div className="flex justify-between text-sm text-muted-foreground mt-2">
            <span>Step {currentStep + 1} of {steps.length}</span>
            <span>{currentStepData.title}</span>
          </div>
        </div>

        <div className="mb-8">
          {renderCurrentStep()}
        </div>

        <OnboardingNavigation
          currentStep={currentStep}
          totalSteps={steps.length}
          onNext={nextStep}
          onPrev={prevStep}
          canGoNext={true}
          canGoPrev={currentStep > 0}
          nextLabel={currentStep === steps.length - 1 ? "Launch Project" : "Next"}
        />
      </div>
    </PageLayout>
  );
}

